import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { Download, X, Share, PlusSquare, Smartphone, Monitor } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PWAInstallPrompt() {
    const { isInstallable, isIOS, showIOSPrompt, installApp } = usePWAInstall();
    const [isVisible, setIsVisible] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);

    useEffect(() => {
        // Verificar si el usuario ya descartó el prompt en esta máquina
        const isDismissed = localStorage.getItem('skymenu_pwa_dismissed');
        const dismissTime = localStorage.getItem('skymenu_pwa_dismiss_time');
        
        // Si fue descartado hace menos de 7 días, no mostrar
        if (isDismissed && dismissTime) {
            const daysSinceDismiss = (Date.now() - parseInt(dismissTime, 10)) / (1000 * 60 * 60 * 24);
            if (daysSinceDismiss < 7) {
                return;
            }
        }

        // Mostrar solo si es instalable o si es un dispositivo iOS (y no está instalada)
        if (isInstallable || showIOSPrompt) {
            // Esperar un momento después de cargar para que no sea invasivo
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isInstallable, showIOSPrompt]);

    const handleDismiss = () => {
        setIsVisible(false);
        // Guardar estado de descarte
        localStorage.setItem('skymenu_pwa_dismissed', 'true');
        localStorage.setItem('skymenu_pwa_dismiss_time', Date.now().toString());
    };

    const handleInstall = async () => {
        if (isIOS) {
            setShowIOSGuide(true);
        } else {
            const success = await installApp();
            if (success) {
                setIsVisible(false);
            }
        }
    };

    // Si no es visible, no renderizar
    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md px-4">
                <motion.div
                    initial={{ opacity: 0, y: 100, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 100, scale: 0.95 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-slate-700/50 shadow-2xl p-5 text-white"
                >
                    {/* Efectos de fondo premium */}
                    <div className="absolute -right-10 -top-10 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl pointer-events-none" />
                    <div className="absolute -left-10 -bottom-10 w-24 h-24 bg-indigo-500/20 rounded-full blur-2xl pointer-events-none" />

                    {!showIOSGuide ? (
                        <div className="flex flex-col gap-4">
                            <button
                                onClick={handleDismiss}
                                className="absolute right-3 top-3 text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-800"
                                aria-label="Cerrar"
                            >
                                <X size={18} />
                            </button>

                            <div className="flex gap-4 items-center pr-6">
                                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-tr from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/20">
                                    <Download size={22} className="text-white animate-bounce" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-[16px] text-white flex items-center gap-1">
                                        Instalar SkyMenu
                                    </h3>
                                    <p className="text-slate-300 text-xs mt-0.5 leading-relaxed">
                                        Descarga la app en tu pantalla de inicio para una carga instantánea y acceso rápido sin navegador.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end mt-1">
                                <button
                                    onClick={handleDismiss}
                                    className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 hover:bg-slate-800"
                                >
                                    Más tarde
                                </button>
                                <button
                                    onClick={handleInstall}
                                    className="px-5 py-2 text-xs font-bold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg shadow-md shadow-blue-500/20 hover:shadow-lg transition-all"
                                >
                                    Instalar Ahora
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            <button
                                onClick={() => setShowIOSGuide(false)}
                                className="absolute right-3 top-3 text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-800"
                                aria-label="Cerrar"
                            >
                                <X size={18} />
                            </button>

                            <div className="pr-6">
                                <h3 className="font-bold text-[16px] text-white flex items-center gap-2">
                                    <Smartphone className="text-blue-400" size={20} />
                                    Instalar en iOS (Safari)
                                </h3>
                                <p className="text-slate-300 text-xs mt-1 leading-relaxed">
                                    Sigue estos simples pasos para añadir SkyMenu a tu pantalla de inicio:
                                </p>
                            </div>

                            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 flex flex-col gap-3 text-xs text-slate-300">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-xs">
                                        1
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        Pulsa el botón de <span className="inline-flex items-center gap-1 bg-slate-700 px-2 py-0.5 rounded text-white"><Share size={12} /> Compartir</span> en la barra inferior de Safari.
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-xs">
                                        2
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        Desplázate hacia abajo y selecciona <span className="inline-flex items-center gap-1 bg-slate-700 px-2 py-0.5 rounded text-white"><PlusSquare size={12} /> "Agregar a inicio"</span>.
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-1">
                                <button
                                    onClick={() => setShowIOSGuide(false)}
                                    className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 hover:bg-slate-800"
                                >
                                    Atrás
                                </button>
                                <button
                                    onClick={handleDismiss}
                                    className="px-5 py-2 text-xs font-bold bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                                >
                                    Entendido
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
